import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Output } from "valibot";

import {
  BotInfoSchema,
  ChatUserFormFieldsSchema,
} from "components/SonicBot/schemas";
import SubmitButton from "components/SubmitButton";
import { ChatUserInputValue } from "shared/api";
import { useConfigContext, useGlobalsContext } from "shared/hooks";
import { Literals } from "shared/types";
import classNames from "shared/utils/classNames";

import InputComponent from "./InputComponent";
import { ChatUserDataElement } from "./types";

import "./index.css";
import "react-phone-input-2/lib/style.css";

type Props = {
  botInfo: Output<typeof BotInfoSchema>;
  chatUserFormFields: Output<typeof ChatUserFormFieldsSchema>;
  loadingChatUserFormFields: boolean;
  copyTexts: Literals;
  handleChatUserCreation: (formData: any) => Promise<void>;
};

function UserDataForm(props: Props) {
  const { isIframe } = useConfigContext();
  const { currentUserFormData } = useGlobalsContext();

  const {
    botInfo,
    chatUserFormFields,
    loadingChatUserFormFields,
    copyTexts,
    handleChatUserCreation,
  } = props;

  const [inputValues, setInputValues] = useState<ChatUserDataElement[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isCaptchaResolved, setIsCaptchaResolved] = useState<boolean>(
    !botInfo.bot_info_config.is_show_recaptcha,
  );

  const handleSubmitRef =
    useRef<(formInputValues: ChatUserDataElement[]) => void>();
  handleSubmitRef.current = (formInputValues: ChatUserDataElement[]) => {
    setErrorMessage("");
    setIsSubmitting(true);
    const formData = formInputValues
      .filter(({ value }) => value !== "")
      .map(({ id, key, value }) => ({
        id,
        key,
        value,
      })) as ChatUserInputValue[];
    handleChatUserCreation(formData)
      .then(err => {
        const errorMessage =
          // @ts-ignore
          err?.payload?.detail ??
          "Sorry, we cannot process your request at the moment. Please contact the support team.";
        setErrorMessage(errorMessage);
      })
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    const newInputValues =
      chatUserFormFields?.data_fields?.map(dataField => {
        return {
          ...dataField,
          value: currentUserFormData?.[dataField.key] || "",
        };
      }) || [];
    setInputValues(newInputValues);

    const notFilledRequired = newInputValues.filter(
      input => input.is_required && !input.value,
    );
    if (currentUserFormData && !notFilledRequired.length) {
      const { current: handleSubmit } = handleSubmitRef;
      if (handleSubmit) {
        handleSubmit(newInputValues);
      }
    }
  }, [chatUserFormFields?.data_fields, currentUserFormData]);

  return (
    <form
      onSubmit={evt => {
        evt.preventDefault();
        const { current: handleSubmit } = handleSubmitRef;
        if (handleSubmit) {
          handleSubmit(inputValues);
        }
      }}
      className={classNames(
        isIframe ? "user-form-container_iframe" : "user-form-container ",
      )}
    >
      {loadingChatUserFormFields && <div>Loading...</div>}
      <div className="inputs-container">
        {inputValues.map(
          (
            { id, key, type, placeholder, is_required, value, is_deleted },
            index,
          ) => (
            <div
              key={id}
              className={classNames(
                isIframe && "flex flex-col gap-1.5",
                !isIframe && !!index && "mt-4",
              )}
            >
              <p className={"label"}>
                {key}
                {is_required && <span className="point">*</span>}
              </p>
              <InputComponent
                id={id}
                type={type}
                value={value || ""}
                generationLanguage={
                  botInfo.bot_prompt_config.generation_language
                }
                setInputValues={setInputValues}
                isRequired={is_required}
                isDeleted={is_deleted}
                placeholder={placeholder}
              />
            </div>
          ),
        )}
        {botInfo.bot_info_config.is_show_recaptcha && (
          <div className="mt-8">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_KEY ?? ""}
              onChange={() => setIsCaptchaResolved(true)}
              onExpired={() => setIsCaptchaResolved(false)}
            />
          </div>
        )}
      </div>
      <div>
        {errorMessage && <p className="error-message">{errorMessage}</p>}
        <SubmitButton isSubmitting={isSubmitting} disabled={!isCaptchaResolved}>
          {isSubmitting
            ? copyTexts.submittingButtonText
            : copyTexts.submitButton}
        </SubmitButton>
      </div>
    </form>
  );
}

export default UserDataForm;
